import { DateInput } from '@apia/components';
import React from 'react';
import { InputProps, Input as TUIInput } from '@apia/theme';
import { FormContext } from '../form/FormContext';
import { useFieldStatesClassNames } from '../hooks';
import { useField } from '../hooks/useField';
import { useValidationClass } from '../hooks/useValidationClass';
import { validationsStore } from '../validationStore';
import { FieldWrapper } from './FieldWrapper';
import { TDefaultFieldType } from './types';

export type TInput = InputProps & TDefaultFieldType;

export const Input = React.memo(
  React.forwardRef<HTMLInputElement, TInput>(
    (
      {
        avoidSemicolon,
        className,
        label,
        onChange: outerOnChange,
        type,
        /**
         * useField
         */
        avoidRegisterIfExists,
        formName: propsFormName,
        initialValue,
        name,
        removeOnUnmount,
        submitValueParser,
        validationClass,
        validationFunction,
        validationRules,
        value,
        ...props
      },
      ref,
    ) => {
      const { name: contextFormName } = React.useContext(FormContext);
      const formName = propsFormName ?? contextFormName;

      const { actualValidationFunction, actualValidationRules } =
        useValidationClass({
          formName,
          name,
          validationClass,
          validationFunction,
          validationRules,
        });

      const {
        error,
        updateValidations,
        onChange,
        isTouched,
        ...validationProps
      } = useField<string>({
        formName,
        name,
        initialValue:
          initialValue ??
          (value as string) ??
          validationsStore.getFieldStore(formName, name)?.value ??
          '',
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });

      const handleOnChange = React.useCallback(
        (ev: string | React.ChangeEvent<HTMLInputElement>) => {
          onChange(ev);
          if (typeof ev === 'string') {
            // Es date
            outerOnChange?.({
              target: ref,
            } as unknown as React.ChangeEvent<HTMLInputElement>);
          } else {
            // Es comun
            outerOnChange?.(ev);
          }
        },
        [onChange, outerOnChange, ref],
      );

      const stateClassName = useFieldStatesClassNames(name);

      return (
        <FieldWrapper
          name={name}
          label={label}
          className={`input ${className ?? ''}`}
          avoidSemicolon={avoidSemicolon}
        >
          {type === 'date' ? (
            <DateInput
              {...props}
              {...validationProps}
              aria-label={props['aria-label'] ?? label}
              onError={undefined}
              ref={ref}
              onChange={handleOnChange}
              value={validationProps.value ?? ''}
              className={stateClassName}
            />
          ) : (
            <TUIInput
              type={type}
              {...props}
              {...validationProps}
              aria-label={props['aria-label'] ?? label}
              ref={ref}
              onChange={handleOnChange}
              value={validationProps.value ?? ''}
              className={stateClassName}
            />
          )}
        </FieldWrapper>
      );
    },
  ),
);

Input.displayName = 'Input';
